import { InputAdornment, Stack, Typography, useTheme } from "@mui/material";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import DatePickerComponent from "./DatePickerComponent";
import DescriptionField from "./DescriptionField";
import { RefObject } from "react";
import FormField from "../../../ui/FormField";
import CurrencyField from "../../../ui/CurrencyField";
import IconCircleWrapper from "../../../ui/IconCircleWrapper";
import { CaretRightIcon, EventRepeatIcon } from "../../../ui/icons";

interface Wallet {
  title: string;
  id: string;
  wallet: {
    id: string;
    name: string;
    balance: number;
    type: string;
  };
}

interface InstallmentConversionFormProps {
  creditCardWallet: Wallet | null;
  datePicker: Dayjs | undefined;
  description: string;
  openDatePicker: boolean;
  numberOfInstallments: number | null;
  setOpenDialogSelectWallets: (open: boolean) => void;
  setOpenDatePicker: (open: boolean) => void;
  setDescription: (description: string) => void;
  setDatePicker: (date: Dayjs | undefined) => void;
  setNumberOfInstallments: (numberOfInstallments: number) => void;
  descriptionInputRef?: RefObject<HTMLInputElement>;
  installmentTermInputRef?: RefObject<HTMLInputElement>;
}

const InstallmentConversionForm = ({
  creditCardWallet,
  datePicker,
  description,
  openDatePicker,
  numberOfInstallments,
  setOpenDialogSelectWallets,
  setOpenDatePicker,
  setDescription,
  setDatePicker,
  setNumberOfInstallments,
  descriptionInputRef,
  installmentTermInputRef,
}: InstallmentConversionFormProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'transactions', 'wallets', 'installments']);

  return (
    <Stack spacing={4} width="100%">
      {/* Credit Card Wallet */}
      <Stack
        direction="row"
        spacing={theme.spacing(2)}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
          <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
            <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
          </IconCircleWrapper>
        </Stack>
        {creditCardWallet ? (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectWallets(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('credit_card_wallet', { ns: 'transactions' })}
            </Typography>
            <Typography variant="h3">
              {creditCardWallet.wallet.name}
            </Typography>
          </Stack>
        ) : (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectWallets(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('credit_card_wallet', { ns: 'transactions' })}
            </Typography>
            <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
          </Stack>
        )}
      </Stack>

      {/* Number of Installments (Kỳ hạn) - same pattern as InstallmentForm.tsx */}
      <FormField
        fullWidth
        type="number"
        variant="standard"
        inputRef={installmentTermInputRef}
        value={numberOfInstallments || ''}
        onChange={(e) => setNumberOfInstallments(Number.parseInt(e.target.value) || 0)}
        label={t('number_of_installments_label', { ns: 'installments' })}
        placeholder={t('number_of_installments_placeholder', { ns: 'installments' })}
        sx={{
          '& .MuiInputBase-input': {
            textAlign: 'right',
          },
          '& .MuiStack-root': {
            flexGrow: 1,
          },
        }}
        slots={{
          input: CurrencyField
        }}
        slotProps={{
          input: {
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0 }}>
                <EventRepeatIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {t('month', { ns: 'common', count: Number(numberOfInstallments || 0) })}
              </InputAdornment>
            ),
            sx: {
              '& > div': {
                fontSize: '14px',
                margin: 0
              }
            }
          },
        }}
      />

      {/* Date */}
      <DatePickerComponent
        datePicker={datePicker}
        setDatePicker={setDatePicker}
        openDatePicker={openDatePicker}
        setOpenDatePicker={setOpenDatePicker}
        clearable={false}
      />

      <DescriptionField
        description={description}
        setDescription={setDescription}
        ref={descriptionInputRef}
      />
    </Stack>
  );
};

export default InstallmentConversionForm;
